import {
  Box,
  Button,
  Card,
  CloseButton,
  Group,
  SegmentedControl,
  Skeleton,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconFilePlus, IconSearch } from "@tabler/icons-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import type {
  ClientRequestSummaryDto,
  ClientRequestView,
  ClientStage,
} from "@mexp/shared";
import { ErrorState } from "@/components/States";
import {
  useClientLibrary,
  useClientOptions,
  useClientQueries,
  useClientRequests,
  useClientSamples,
} from "./api";
import {
  ClientCertificates,
  ClientMedia,
  FiledDossiers,
  libraryCounts,
} from "./ClientLibrary";
import { ClientQueries } from "./ClientQueries";
import { byRecency, byUrgency, RequestRow } from "./RequestRow";
import {
  countriesOf,
  filtering,
  NO_FILTERS,
  passesFilters,
  readRequisitionFilters,
  RequisitionFilters,
  writeRequisitionFilters,
  type RequisitionFilterValues,
} from "./RequisitionFilters";
import { STAGE_ORDER } from "./stage";
import { SampleRow } from "./SampleTracker";

const VIEWS: ClientRequestView[] = ["all", "open", "ready", "cancelled"];

/** Every stage, cancelled too: All dossiers keeps cancelled requisitions. */
const FILTER_STAGES: ClientStage[] = [...STAGE_ORDER, "cancelled"];

/**
 * Requisitions raised in the portal; dossiers and certificates Makin keeps for the company; the queries raised on
 * released dossiers; approved artworks and 3D images; samples asked for.
 */
const TABS = [
  "requisitions",
  "filed",
  "certificates",
  "queries",
  "artworks",
  "samples",
] as const;
type Tab = (typeof TABS)[number];

function matches(r: ClientRequestSummaryDto, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [r.productName, r.genericName, r.country, r.code, r.category, r.dosageForm].some(
    (v) => v?.toLowerCase().includes(needle),
  );
}

/** Open dossiers most urgent first, then the rest most recent first. */
function ordered(items: ClientRequestSummaryDto[]): ClientRequestSummaryDto[] {
  const open = items
    .filter((r) => r.stage !== "completed" && r.stage !== "cancelled")
    .sort(byUrgency);
  const closed = items
    .filter((r) => r.stage === "completed" || r.stage === "cancelled")
    .sort(byRecency);
  return [...open, ...closed];
}

/**
 * Every dossier the company has asked Makin for, filterable by state and searchable, and, on their own tabs, the
 * dossiers Makin filed for the company and its certificates (with Makin's accreditation certificates).
 */
export function ClientDossiersPage() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS as string[]).includes(params.get("view") ?? "")
    ? (params.get("view") as ClientRequestView)
    : "all";
  const q = params.get("q") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(params.get("tab") ?? "")
    ? (params.get("tab") as Tab)
    : "requisitions";
  const options = useClientOptions();
  const list = useClientRequests(view);
  const library = useClientLibrary();
  const libCounts = libraryCounts(library.data);
  const samples = useClientSamples();
  const queries = useClientQueries();
  // Samples on their way first, then the delivered ones; the latest change first in each.
  const sampleRows = useMemo(() => {
    const items = samples.data ?? [];
    return [
      ...items.filter((s) => s.status !== "delivered"),
      ...items.filter((s) => s.status === "delivered"),
    ];
  }, [samples.data]);

  const filters = readRequisitionFilters(params, FILTER_STAGES);
  const narrowed = filtering(filters);
  const update = (
    next: { view?: ClientRequestView; q?: string } & Partial<RequisitionFilterValues>,
  ) => {
    const merged = { view: next.view ?? view, q: next.q ?? q };
    const out = new URLSearchParams();
    if (tab !== "requisitions") out.set("tab", tab);
    if (merged.view !== "all") out.set("view", merged.view);
    if (merged.q.trim()) out.set("q", merged.q);
    writeRequisitionFilters(out, { ...filters, ...next });
    setParams(out, { replace: true });
  };

  const selectTab = (next: string | null) => {
    const out = new URLSearchParams(params);
    if (!next || next === "requisitions") out.delete("tab");
    else out.set("tab", next);
    setParams(out);
  };
  const count = (n: number | undefined) => (n === undefined ? "" : ` · ${n}`);

  const counts = list.data?.counts;
  const total = counts?.all;
  const rows = ordered(
    (list.data?.items ?? []).filter(
      (r) => matches(r, q) && passesFilters(r, filters),
    ),
  );

  if (list.isError)
    return (
      <ErrorState error={list.error} onRetry={() => void list.refetch()} />
    );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Title
            order={1}
            fz={{ base: 26, sm: 32 }}
            style={{ letterSpacing: "-0.015em" }}
          >
            All dossiers
          </Title>
          <Text c="dimmed">
            {/* Each tab carries its own count. */}
            {options.data?.company.name ?? " "}
          </Text>
        </Stack>
        <Button
          component={Link}
          to="/requests/new"
          leftSection={<IconFilePlus size={16} />}
        >
          New requisition
        </Button>
      </Group>

      <Tabs value={tab} onChange={selectTab} keepMounted={false}>
        <Tabs.List mb="lg">
          <Tabs.Tab value="requisitions">Requisitions{count(total)}</Tabs.Tab>
          <Tabs.Tab value="filed">
            Filed dossiers{count(libCounts.dossiers)}
          </Tabs.Tab>
          <Tabs.Tab value="certificates">
            Certificates{count(libCounts.certificates)}
          </Tabs.Tab>
          <Tabs.Tab value="queries">
            Queries{count(queries.data?.length)}
          </Tabs.Tab>
          <Tabs.Tab value="artworks">
            Artworks & 3D images{count(libCounts.media)}
          </Tabs.Tab>
          {/* Only for companies that asked for a sample (or a link that opens the tab). */}
          {(!!samples.data?.length || tab === "samples") && (
            <Tabs.Tab value="samples">
              Samples{count(samples.data?.length)}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="requisitions">
          <Card p={0}>
            <Group justify="space-between" p="md" wrap="wrap" gap="sm">
              <SegmentedControl
                value={view}
                onChange={(v) => update({ view: v as ClientRequestView })}
                data={[
                  {
                    value: "all",
                    label: `All${total !== undefined ? ` · ${total}` : ""}`,
                  },
                  {
                    value: "open",
                    label: `Open${counts ? ` · ${counts.open}` : ""}`,
                  },
                  {
                    value: "ready",
                    label: `Ready${counts ? ` · ${counts.ready}` : ""}`,
                  },
                  {
                    value: "cancelled",
                    label: `Cancelled${counts ? ` · ${counts.cancelled}` : ""}`,
                  },
                ]}
              />
              <TextInput
                aria-label="Search dossiers"
                placeholder="Brand/generic name, country or code"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  q ? (
                    <CloseButton
                      size="sm"
                      aria-label="Clear search"
                      onClick={() => update({ q: "" })}
                    />
                  ) : null
                }
                value={q}
                onChange={(e) => update({ q: e.currentTarget.value })}
                w={{ base: "100%", sm: 300 }}
              />
            </Group>
            <Box px="md" pb="md">
              <RequisitionFilters
                value={filters}
                onChange={(f) => update(f)}
                stages={FILTER_STAGES}
                countries={countriesOf(list.data?.items ?? [], filters.country)}
              />
            </Box>
            <Box px={6} pb={6}>
              {list.isLoading ? (
                <Stack gap="xs" p="sm">
                  <Skeleton h={56} />
                  <Skeleton h={56} />
                  <Skeleton h={56} />
                </Stack>
              ) : rows.length ? (
                <Stack gap={2}>
                  {rows.map((r) => (
                    <RequestRow key={r.id} r={r} />
                  ))}
                </Stack>
              ) : q.trim() || narrowed ? (
                <Stack gap="xs" p="md" align="flex-start">
                  <Text fz="sm">
                    No dossiers match{" "}
                    {q.trim() ? `“${q.trim()}”` : "these filters"}
                    {q.trim() && narrowed ? " with these filters" : ""}.
                  </Text>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    onClick={() => update({ q: "", ...NO_FILTERS })}
                  >
                    Clear the search and filters
                  </Button>
                </Stack>
              ) : (
                <Stack gap="xs" p="md" align="flex-start">
                  <Text fz="sm" c="dimmed">
                    {view === "open"
                      ? "No open dossiers right now."
                      : view === "ready"
                        ? "Nothing has been released yet. Approved dossiers appear here."
                        : view === "cancelled"
                          ? "No cancelled dossiers."
                          : "No dossiers yet. Send a requisition and it appears here."}
                  </Text>
                  {view === "all" && (
                    <Button
                      component={Link}
                      to="/requests/new"
                      size="compact-sm"
                      variant="subtle"
                      leftSection={<IconFilePlus size={14} />}
                    >
                      New requisition
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="filed">
          <FiledDossiers library={library} />
        </Tabs.Panel>
        <Tabs.Panel value="certificates">
          <ClientCertificates
            library={library}
            company={options.data?.company.name}
          />
        </Tabs.Panel>
        <Tabs.Panel value="queries">
          <ClientQueries />
        </Tabs.Panel>
        <Tabs.Panel value="artworks">
          <ClientMedia library={library} company={options.data?.company.name} />
        </Tabs.Panel>
        <Tabs.Panel value="samples">
          <Card p={0}>
            <Box p={6}>
              {samples.isLoading ? (
                <Stack gap="xs" p="sm">
                  <Skeleton h={56} />
                  <Skeleton h={56} />
                </Stack>
              ) : samples.isError ? (
                <ErrorState
                  error={samples.error}
                  onRetry={() => void samples.refetch()}
                />
              ) : sampleRows.length ? (
                <Stack gap={2}>
                  {sampleRows.map((s) => (
                    <SampleRow key={s.requestId} s={s} />
                  ))}
                </Stack>
              ) : (
                <Stack gap="xs" p="md" align="flex-start">
                  <Text fz="sm" c="dimmed">
                    No samples yet. Ask for one, plain or in commercial
                    packaging, when you send a requisition.
                  </Text>
                  <Button
                    component={Link}
                    to="/requests/new"
                    size="compact-sm"
                    variant="subtle"
                    leftSection={<IconFilePlus size={14} />}
                  >
                    New requisition
                  </Button>
                </Stack>
              )}
            </Box>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
